import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  FileText,
  CheckCircle,
  Users,
  Trash2,
  Filter,
  X,
  ChevronDown,
  Calendar,
  Download,
  Copy,
  ChevronRight,
  Clock,
  Eye,
  RotateCcw,
  Plus,
  GripVertical,
  Shuffle,
  Share2,
  Search,
  Check,
  Loader2,
  Link as LinkIcon,
  Mail,
  Send,
  BarChart3,
  Award,
  Pencil,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
} from "lucide-react";
import { toast } from "react-toastify";
import "./TestManagement.scss";
import axiosInstance from "../../axiosInstace";
import LoadingScreen from "../../LoadingScreen/LoadingScreen";
import WarningPopup from "../../components/WarningPopup/WarningPopup";
import ButtonWithIcon from "../../components/ButtonwithIcon/ButtonWithIcon";
import { useAuth } from "../../contexts/AuthContext";
import TestResults from "../TestResults/TestResults";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const parseDurationToHM = (raw) => {
  if (!raw) return { hours: 0, minutes: 0 };
  if (typeof raw === "object" && raw !== null && "seconds" in raw) {
    const totalSecs = Number(raw.seconds) || 0;
    return {
      hours: Math.floor(totalSecs / 3600),
      minutes: Math.floor((totalSecs % 3600) / 60),
    };
  }
  if (typeof raw === "string") {
    const h = raw.match(/(\d+)H/);
    const m = raw.match(/(\d+)M/);
    return {
      hours: h ? parseInt(h[1], 10) : 0,
      minutes: m ? parseInt(m[1], 10) : 0,
    };
  }
  return { hours: 0, minutes: 0 };
};

// ✅ Normalises a question's answerType into the enum shape the backend
// expects (SINGLE, RATING, MULTI_CHOICE, SINGLE_CHOICE). The GET /tests/{id}
// endpoint can return human-readable labels like "Multi Choice" — naively
// calling .toUpperCase() on that leaves the space in place ("MULTI CHOICE"),
// which the backend enum rejects. This collapses whitespace into underscores
// first so both "Multi Choice" and "MULTI_CHOICE" normalise the same way.
const normaliseAnswerType = (raw) => {
  if (!raw) return "";
  return String(raw).trim().toUpperCase().replace(/\s+/g, "_");
};

// ✅ Handles whatever the API returns
const normaliseAttemptType = (raw) => {
  if (!raw) return "ONE_TIME";
  const v = raw.toUpperCase();
  if (v === "MULTIPLE") return "MULTIPLE";
  if (v === "UNLIMITED") return "UNLIMITED";
  return "ONE_TIME";
};

const buildISODuration = ({ hours, minutes }) => {
  const h = parseInt(hours, 10) || 0;
  const m = parseInt(minutes, 10) || 0;
  if (h === 0 && m === 0) return null;
  return `PT${h > 0 ? h + "H" : ""}${m > 0 ? m + "M" : ""}`;
};

// Unique ID generator for new items (client-side only, negative to avoid clash with real IDs)
let _uid = -1;
const uid = () => _uid--;

const copyToClipboard = async (text) => {
  if (!text) return false;
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch (err) {
    console.error("Clipboard copy failed:", err);
    return false;
  }
};

// ---------------------------------------------------------------------------
// Shared sub-components
// ---------------------------------------------------------------------------

const ToggleRow = ({ label, value, onChange, disabled = false }) => (
  <div className={`toggle-row ${disabled ? "toggle-row--disabled" : ""}`}>
    <span className="toggle-row-label">{label}</span>
    <button
      type="button"
      className={`toggle-switch ${value ? "toggle-switch--on" : ""}`}
      onClick={() => onChange(!value)}
      aria-pressed={value}
      disabled={disabled}
    >
      <span className="toggle-thumb" />
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// InstantTooltip – zero-delay tooltip (bypasses the browser's native
// title-attribute delay so the label appears the instant you hover/focus).
//
// Rendered via a portal into document.body: a horizontally-scrolling
// `.table-container` (overflow-x: auto) can still clip a normally-positioned
// bubble that pokes above/below the row on narrow viewports. Instead the
// bubble escapes to <body> and is positioned from the anchor's real
// getBoundingClientRect() coordinates, so no ancestor's overflow or
// z-index can clip or bury it.
// ---------------------------------------------------------------------------
const InstantTooltip = ({ label, children }) => {
  const anchorRef = useRef(null);
  const [coords, setCoords] = useState(null); // null = hidden

  const updatePosition = () => {
    const el = anchorRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setCoords({
      top: rect.top,
      left: rect.left + rect.width / 2,
    });
  };

  const show = () => updatePosition();
  const hide = () => setCoords(null);

  // While visible, keep the bubble glued to its anchor through scrolling
  // (including the scrollable .table-container) and window resizes —
  // otherwise a fixed-position portal node would drift away from the
  // button that opened it.
  useEffect(() => {
    if (!coords) return undefined;
    const onReposition = () => updatePosition();
    window.addEventListener("scroll", onReposition, true);
    window.addEventListener("resize", onReposition);
    return () => {
      window.removeEventListener("scroll", onReposition, true);
      window.removeEventListener("resize", onReposition);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [coords !== null]);

  return (
    <span
      className="instant-tooltip"
      ref={anchorRef}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      {coords &&
        createPortal(
          <span
            className="instant-tooltip-bubble instant-tooltip-bubble--portal"
            role="tooltip"
            style={{ top: coords.top, left: coords.left }}
          >
            {label}
          </span>,
          document.body,
        )}
    </span>
  );
};

// ---------------------------------------------------------------------------
// ActionIconButton – row action icon with a refreshed, distinct color per
// action and an InstantTooltip label instead of the native `title` delay
// ---------------------------------------------------------------------------
const ACTION_ICONS = {
  view: Eye,
  edit: Pencil,
  results: BarChart3,
  duplicate: Copy,
  share: Share2,
  "private-link": Mail,
  "public-link": LinkIcon,
  delete: Trash2,
};

const ActionIconButton = ({ variant, label, disabled, onClick }) => {
  const Icon = ACTION_ICONS[variant];
  return (
    <InstantTooltip label={label}>
      <button
        type="button"
        className={`action-icon-btn action-icon-btn--${variant}`}
        disabled={disabled}
        onClick={onClick}
        aria-label={label}
      >
        {Icon && <Icon size={16} />}
      </button>
    </InstantTooltip>
  );
};

// ---------------------------------------------------------------------------
// AttemptTypeField – reusable attempt type + max attempts block
// ---------------------------------------------------------------------------
const AttemptTypeField = ({ namePrefix, form, setField }) => (
  <>
    <div className="form-group">
      <label>
        <RotateCcw
          size={13}
          style={{ marginRight: 4, verticalAlign: "middle" }}
        />
        Test Attempt Type
      </label>
      <div className="attempt-type-group">
        {["ONE_TIME", "MULTIPLE", "UNLIMITED"].map((type) => (
          <label key={type} className="attempt-radio">
            <input
              type="radio"
              name={`${namePrefix}-attemptType`}
              value={type}
              checked={form.testAttemptType === type}
              onChange={() => setField("testAttemptType", type)}
            />
            <span className="attempt-radio-label">
              {type === "ONE_TIME"
                ? "Single"
                : type === "MULTIPLE"
                  ? "Multiple"
                  : "Unlimited"}
            </span>
          </label>
        ))}
      </div>
    </div>

    {form.testAttemptType === "MULTIPLE" && (
      <div className="form-group">
        <label>Max Attempts</label>
        <input
          type="number"
          min={1}
          value={form.maxAttempts}
          onChange={(e) => setField("maxAttempts", e.target.value)}
          placeholder="e.g. 3"
          style={{ maxWidth: 120 }}
        />
      </div>
    )}
  </>
);

// ---------------------------------------------------------------------------
// VisibilityToggles – reusable result-visibility toggles block
// "Show results to user" is the parent of the 5 toggles below it:
//   - Turning the parent ON  -> auto-selects (checks) every child
//   - Turning the parent OFF -> auto-deselects (unchecks) every child
//   - Children can still be toggled individually by the user while the
//     parent is ON (manual deselect of just that child)
//   - While the parent is OFF, the children are disabled (nothing to show)
// ---------------------------------------------------------------------------
const VISIBILITY_CHILD_FIELDS = [
  "showScore",
  "showRecommendation",
  "showInterpretation",
  "showSectionRecommendation",
  "showSectionInterpretation",
];

// ---------------------------------------------------------------------------
// Default form state factory – single source of truth for all popups
// ---------------------------------------------------------------------------
const defaultFormState = (overrides = {}) => ({
  testTitle: "",
  description: "",
  linkExpiryDate: "",
  durationHours: 0,
  durationMinutes: 0,
  testAttemptType: "ONE_TIME",
  maxAttempts: 1,
  showResultsToUser: true,
  showScore: true,
  showRecommendation: true,
  showInterpretation: true,
  showSectionRecommendation: true,
  showSectionInterpretation: true,
  ...overrides,
});

const ANSWER_TYPES = [
  { value: "SINGLE_CHOICE", label: "Single Choice" },
  { value: "MULTI_CHOICE", label: "Multiple Choice" },
  { value: "SINGLE", label: "Single" },
  { value: "RATING", label: "Rating" },
];

const OPTION_VALUES = [
  "YES",
  "NO",
  "ONE",
  "TWO",
  "THREE",
  "FOUR",
  "FIVE",
  "SIX",
  "SEVEN",
  "EIGHT",
  "NINE",
  "TEN",
  "CUSTOM",
];

// ---------------------------------------------------------------------------
// AnswerOptionRow
// ---------------------------------------------------------------------------
const AnswerOptionRow = ({ opt, onChange, onRemove }) => (
  <div className="answer-option-row">
    <input
      type="text"
      className="option-text-input"
      value={opt.answerText}
      onChange={(e) => onChange({ ...opt, answerText: e.target.value })}
      placeholder="Option text"
    />
    <select
      className="option-value-select"
      value={opt.optionValue}
      onChange={(e) => onChange({ ...opt, optionValue: e.target.value })}
    >
      {OPTION_VALUES.map((v) => (
        <option key={v} value={v}>
          {v}
        </option>
      ))}
    </select>
    <input
      type="number"
      className="option-score-input"
      value={opt.score ?? ""}
      onChange={(e) =>
        onChange({
          ...opt,
          score: e.target.value === "" ? null : Number(e.target.value),
        })
      }
      placeholder="Score"
    />
    <button
      type="button"
      className="remove-option-btn"
      onClick={onRemove}
      title="Remove option"
    >
      <X size={12} />
    </button>
  </div>
);

// ---------------------------------------------------------------------------
// QuestionEditor – editable question card
// ---------------------------------------------------------------------------
const QuestionEditor = ({ question, onChange, onRemove, index }) => {
  const [expanded, setExpanded] = useState(true);

  const updateOption = (optIdx, updated) => {
    const newOpts = question.answerOptions.map((o, i) =>
      i === optIdx ? updated : o,
    );
    onChange({ ...question, answerOptions: newOpts });
  };

  const addOption = () => {
    onChange({
      ...question,
      answerOptions: [
        ...question.answerOptions,
        { _id: uid(), answerText: "", optionValue: "YES", score: 0 },
      ],
    });
  };

  const removeOption = (optIdx) => {
    onChange({
      ...question,
      answerOptions: question.answerOptions.filter((_, i) => i !== optIdx),
    });
  };

  // FIX: was checking "MULTIPLE_CHOICE" but the enum value is "MULTI_CHOICE"
  const showOptions =
    question.answerType === "SINGLE_CHOICE" ||
    question.answerType === "MULTI_CHOICE";

  return (
    <div className="question-editor-card">
      <div className="question-editor-header">
        <button
          type="button"
          className={`qe-expand-btn ${expanded ? "qe-expand-btn--open" : ""}`}
          onClick={() => setExpanded(!expanded)}
          title={expanded ? "Collapse question" : "Expand question"}
        >
          <ChevronRight size={13} />
        </button>
        <span className="qe-index">Q{index + 1}</span>
        <input
          type="text"
          className="qe-text-input"
          value={question.questionText}
          onChange={(e) =>
            onChange({ ...question, questionText: e.target.value })
          }
          placeholder={`Question ${index + 1}`}
        />
        <select
          className="qe-type-select"
          value={question.answerType}
          onChange={(e) =>
            onChange({ ...question, answerType: e.target.value })
          }
        >
          {ANSWER_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="qe-remove-btn"
          onClick={onRemove}
          title="Remove question"
        >
          <Trash2 size={13} />
        </button>
      </div>

      {expanded && showOptions && (
        <div className="qe-options-body">
          <div className="options-header-row">
            <span className="options-col-label">Answer text</span>
            <span className="options-col-label">Value</span>
            <span className="options-col-label">Score</span>
            <span />
          </div>
          {question.answerOptions.map((opt, oi) => (
            <AnswerOptionRow
              key={opt._id ?? opt.answerOptionId ?? oi}
              opt={opt}
              onChange={(updated) => updateOption(oi, updated)}
              onRemove={() => removeOption(oi)}
            />
          ))}
          <button
            type="button"
            className="add-option-btn"
            onClick={addOption}
            title="Add a new answer option"
          >
            <Plus size={12} /> Add option
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// SectionEditor – full editable section with questions
// ---------------------------------------------------------------------------
const SectionEditor = ({ section, onChange, onRemove, index }) => {
  const [collapsed, setCollapsed] = useState(false);

  const updateQuestion = (qIdx, updated) => {
    onChange({
      ...section,
      questions: section.questions.map((q, i) => (i === qIdx ? updated : q)),
    });
  };

  const addQuestion = () => {
    onChange({
      ...section,
      questions: [
        ...section.questions,
        {
          _id: uid(),
          questionText: "",
          answerType: "SINGLE_CHOICE",
          questionOrder: section.questions.length + 1,
          answerOptions: [],
        },
      ],
    });
  };

  const removeQuestion = (qIdx) => {
    onChange({
      ...section,
      questions: section.questions.filter((_, i) => i !== qIdx),
    });
  };

  return (
    <div className="section-editor-card">
      {/* Section header */}
      <div className="section-editor-header">
        <GripVertical size={14} className="section-drag-handle" />
        <button
          type="button"
          className={`se-collapse-btn ${collapsed ? "" : "se-collapse-btn--open"}`}
          onClick={() => setCollapsed(!collapsed)}
          title={collapsed ? "Expand section" : "Collapse section"}
        >
          <ChevronRight size={14} />
        </button>
        <span className="se-index">§{index + 1}</span>
        <input
          type="text"
          className="se-title-input"
          value={section.sectionTitle}
          onChange={(e) =>
            onChange({ ...section, sectionTitle: e.target.value })
          }
          placeholder={`Section ${index + 1} title`}
        />
        <label className="se-randomize-label" title="Randomize question order">
          <Shuffle size={13} />
          <input
            type="checkbox"
            checked={!!section.randomizeQuestions}
            onChange={(e) =>
              onChange({ ...section, randomizeQuestions: e.target.checked })
            }
          />
          <span>Randomize</span>
        </label>
        <button
          type="button"
          className="se-remove-btn"
          onClick={onRemove}
          title="Remove section"
        >
          <Trash2 size={14} />
        </button>
      </div>

      {!collapsed && (
        <div className="section-editor-body">
          {section.questions.length === 0 && (
            <p className="no-questions-msg">No questions yet. Add one below.</p>
          )}
          {section.questions.map((q, qi) => (
            <QuestionEditor
              key={q._id ?? q.questionId ?? qi}
              question={q}
              index={qi}
              onChange={(updated) => updateQuestion(qi, updated)}
              onRemove={() => removeQuestion(qi)}
            />
          ))}
          <button
            type="button"
            className="add-question-btn"
            onClick={addQuestion}
            title="Add a new question to this section"
          >
            <Plus size={13} /> Add Question
          </button>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// DuplicatePopup – full create-like editor
// ---------------------------------------------------------------------------
const DuplicatePopup = ({ test, onCancel, onSuccess }) => {
  const [loadingTest, setLoadingTest] = useState(true);
  const [fullTest, setFullTest] = useState(null);

  const [form, setFormState] = useState(defaultFormState({ testTitle: "" }));
  const setField = (key, value) =>
    setFormState((prev) => ({ ...prev, [key]: value }));

  const [sections, setSections] = useState([]);
  const [duplicating, setDuplicating] = useState(false);
  const [activeTab, setActiveTab] = useState("details");

  // ── Fetch full test ────────────────────────────────────────────────────────
  useEffect(() => {
    const fetchFullTest = async () => {
      setLoadingTest(true);
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_USER_URL}/tests/${test.testId}`,
        );
        const data = res.data?.data;
        setFullTest(data);

        const dur = parseDurationToHM(data.testduration);
        setFormState(
          defaultFormState({
            testTitle: `${data.testTitle} (Copy)`,
            description: data.description || "",
            linkExpiryDate: data.linkExpiryDate
              ? new Date(data.linkExpiryDate).toISOString().slice(0, 16)
              : "",
            durationHours: dur.hours,
            durationMinutes: dur.minutes,
            testAttemptType: normaliseAttemptType(data.testAttemptType),
            maxAttempts: data.maxAttempts || 1,
            showResultsToUser: data.showResultsToUser ?? true,
            showScore: data.showScore ?? true,
            showRecommendation: data.showRecommendation ?? true,
            showInterpretation: data.showInterpretation ?? true,
            showSectionRecommendation: data.showSectionRecommendation ?? true,
            showSectionInterpretation: data.showSectionInterpretation ?? true,
          }),
        );

        setSections(
          (data.sections || []).map((s) => ({
            ...s,
            _id: uid(),
            questions: (s.questions || []).map((q) => ({
              ...q,
              _id: uid(),
              answerType: normaliseAnswerType(q.answerType),
              answerOptions: (q.answerOptions || []).map((o) => ({
                ...o,
                _id: uid(),
              })),
            })),
          })),
        );
      } catch (err) {
        console.error("Failed to fetch test details:", err);
        toast.error("Failed to load test details");
        onCancel();
      } finally {
        setLoadingTest(false);
      }
    };
    fetchFullTest();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test.testId]);

  // ── Section helpers ────────────────────────────────────────────────────────
  const addSection = () => {
    setSections((prev) => [
      ...prev,
      {
        _id: uid(),
        sectionTitle: "",
        sectionOrder: prev.length + 1,
        randomizeQuestions: false,
        questions: [],
        sectionInterpretation: [],
        sectionRecommendations: [],
      },
    ]);
  };

  const updateSection = (idx, updated) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? updated : s)));
  };

  const removeSection = (idx) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  // ── Submit ─────────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    if (!form.testTitle.trim()) {
      toast.error("Test title is required");
      return;
    }
    if (sections.length === 0) {
      toast.error("At least one section is required");
      return;
    }

    // Validate every question has an answer type, and every choice-based
    // question has answer options with a value, before building the payload.
    for (const s of sections) {
      for (const q of s.questions || []) {
        if (!q.answerType) {
          toast.error(
            `Question "${q.questionText || "(untitled)"}" in section "${s.sectionTitle || "(untitled)"}" is missing an answer type`,
          );
          return;
        }
        const needsOptions =
          q.answerType === "SINGLE_CHOICE" || q.answerType === "MULTI_CHOICE";
        if (needsOptions) {
          if (!q.answerOptions || q.answerOptions.length === 0) {
            toast.error(
              `Question "${q.questionText || "(untitled)"}" needs at least one answer option`,
            );
            return;
          }
          for (const o of q.answerOptions) {
            if (!o.optionValue) {
              toast.error(
                `An answer option in "${q.questionText || "(untitled)"}" is missing a value`,
              );
              return;
            }
          }
        }
      }
    }

    const isoDuration = buildISODuration({
      hours: form.durationHours,
      minutes: form.durationMinutes,
    });

    const mappedSections = sections.map((s, si) => ({
      sectionId: s.sectionId || null,
      sectionTitle: s.sectionTitle,
      sectionOrder: si + 1,
      randomizeQuestions: !!s.randomizeQuestions,
      questions: (s.questions || []).map((q, qi) => ({
        questionId: q.questionId || null,
        questionText: q.questionText,
        answerType: normaliseAnswerType(q.answerType),
        questionOrder: qi + 1,
        answerOptions: (q.answerOptions || []).map((o) => ({
          answerOptionId: o.answerOptionId || null,
          answerText: o.answerText,
          optionValue: (o.optionValue || "").toUpperCase(),
          score: o.score ?? 0,
        })),
      })),
      sectionInterpretations: s.sectionInterpretation || [],
      sectionRecommendations: s.sectionRecommendations || [],
    }));

    const payload = {
      testId: fullTest.testId,
      testTitle: form.testTitle,
      description: form.description,
      linkExpiryDate: form.linkExpiryDate
        ? new Date(form.linkExpiryDate).toISOString()
        : null,
      testduration: isoDuration,
      testAttemptType: form.testAttemptType,
      maxAttempts:
        form.testAttemptType === "MULTIPLE"
          ? parseInt(form.maxAttempts, 10) || 1
          : null,
      showResultsToUser: form.showResultsToUser,
      showScore: form.showScore,
      showRecommendation: form.showRecommendation,
      showInterpretation: form.showInterpretation,
      showSectionRecommendation: form.showSectionRecommendation,
      showSectionInterpretation: form.showSectionInterpretation,
      sections: mappedSections,
      interpretations: fullTest?.interpretations || [],
      recommendations: fullTest?.recommendations || [],
    };

    setDuplicating(true);
    try {
      await axiosInstance.post(
        `${process.env.REACT_APP_API_USER_URL}/tests/duplicate`,
        payload,
      );
      toast.success("✅ Test duplicated successfully!");
      onSuccess();
    } catch (err) {
      console.error("Failed to duplicate test:", err);
      if (err.response?.status === 409) {
        toast.error(err.response?.data?.message || "Conflict occurred");
      } else {
        toast.error("❌ Failed to duplicate test");
      }
    } finally {
      setDuplicating(false);
    }
  };

  const totalQ = sections.reduce((a, s) => a + s.questions.length, 0);

  return (
    <div className="duplicate-popup-overlay" onClick={onCancel}>
      <div
        className="duplicate-popup duplicate-popup--full"
        onClick={(e) => e.stopPropagation()}
      >
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="duplicate-popup-header">
          <div className="duplicate-popup-title">
            <Copy size={20} />
            <h2>Duplicate Test</h2>
          </div>
          <button
            className="duplicate-popup-close"
            onClick={onCancel}
            title="Close without duplicating"
          >
            <X size={20} />
          </button>
        </div>
        <p className="duplicate-popup-subtitle">
          Creating a copy of <strong>{test.title}</strong>
        </p>

        {loadingTest ? (
          <div className="duplicate-loading">
            <div className="duplicate-spinner" />
            <span>Loading test details…</span>
          </div>
        ) : (
          <>
            {/* ── Tab bar ──────────────────────────────────────────────── */}
            <div className="dup-tab-bar">
              <button
                type="button"
                className={`dup-tab ${activeTab === "details" ? "dup-tab--active" : ""}`}
                onClick={() => setActiveTab("details")}
                title="Edit basic test details"
              >
                <FileText size={14} />
                Test Details
              </button>
              <button
                type="button"
                className={`dup-tab ${activeTab === "sections" ? "dup-tab--active" : ""}`}
                onClick={() => setActiveTab("sections")}
                title="Edit sections and questions"
              >
                <CheckCircle size={14} />
                Sections &amp; Questions
                <span className="dup-tab-badge">
                  {sections.length}S · {totalQ}Q
                </span>
              </button>
            </div>

            {/* ── Body ─────────────────────────────────────────────────── */}
            <div className="duplicate-popup-body duplicate-popup-body--tabs">
              {/* ===== TAB: TEST DETAILS ===== */}
              {activeTab === "details" && (
                <div className="dup-details-grid">
                  {/* Left – basic info */}
                  <div className="dup-details-col">
                    <p className="dup-section-label">Basic Info</p>

                    <div className="form-group">
                      <label>
                        Test Title <span className="required">*</span>
                      </label>
                      <input
                        type="text"
                        value={form.testTitle}
                        onChange={(e) => setField("testTitle", e.target.value)}
                        placeholder="Enter test title"
                      />
                    </div>

                    <div className="form-group">
                      <label>Description</label>
                      <textarea
                        value={form.description}
                        onChange={(e) =>
                          setField("description", e.target.value)
                        }
                        placeholder="Enter description (optional)"
                        rows={3}
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <Calendar
                          size={13}
                          style={{ marginRight: 4, verticalAlign: "middle" }}
                        />
                        Link Expiry Date
                      </label>
                      <input
                        type="datetime-local"
                        value={form.linkExpiryDate}
                        onChange={(e) =>
                          setField("linkExpiryDate", e.target.value)
                        }
                      />
                    </div>

                    <div className="form-group">
                      <label>
                        <Clock
                          size={13}
                          style={{ marginRight: 4, verticalAlign: "middle" }}
                        />
                        Test Duration
                      </label>
                      <div className="duration-inputs">
                        <div className="duration-field">
                          <input
                            type="number"
                            min={0}
                            max={23}
                            value={form.durationHours}
                            onChange={(e) =>
                              setField("durationHours", e.target.value)
                            }
                          />
                          <span className="duration-unit">hrs</span>
                        </div>
                        <span className="duration-sep">:</span>
                        <div className="duration-field">
                          <input
                            type="number"
                            min={0}
                            max={59}
                            value={form.durationMinutes}
                            onChange={(e) =>
                              setField("durationMinutes", e.target.value)
                            }
                          />
                          <span className="duration-unit">min</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Right – attempt type + visibility */}
                  <div className="dup-details-col">
                    <p className="dup-section-label">Attempt Settings</p>

                    <AttemptTypeField
                      namePrefix="dup"
                      form={form}
                      setField={setField}
                    />

                    <p className="dup-section-label" style={{ marginTop: 16 }}>
                      <Eye
                        size={13}
                        style={{ marginRight: 4, verticalAlign: "middle" }}
                      />
                      Result Visibility
                    </p>
                    <div className="toggles-card">
                      <ToggleRow
                        label="Show results to user"
                        value={form.showResultsToUser}
                        onChange={(v) => {
                          setField("showResultsToUser", v);
                          // Parent controls all children: ON selects them
                          // all, OFF deselects them all.
                          VISIBILITY_CHILD_FIELDS.forEach((key) =>
                            setField(key, v),
                          );
                        }}
                      />
                      <ToggleRow
                        label="Show score"
                        value={form.showScore}
                        onChange={(v) => setField("showScore", v)}
                        disabled={!form.showResultsToUser}
                      />
                      <ToggleRow
                        label="Show recommendation"
                        value={form.showRecommendation}
                        onChange={(v) => setField("showRecommendation", v)}
                        disabled={!form.showResultsToUser}
                      />
                      <ToggleRow
                        label="Show interpretation"
                        value={form.showInterpretation}
                        onChange={(v) => setField("showInterpretation", v)}
                        disabled={!form.showResultsToUser}
                      />
                      <ToggleRow
                        label="Show section recommendation"
                        value={form.showSectionRecommendation}
                        onChange={(v) =>
                          setField("showSectionRecommendation", v)
                        }
                        disabled={!form.showResultsToUser}
                      />
                      <ToggleRow
                        label="Show section interpretation"
                        value={form.showSectionInterpretation}
                        onChange={(v) =>
                          setField("showSectionInterpretation", v)
                        }
                        disabled={!form.showResultsToUser}
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* ===== TAB: SECTIONS & QUESTIONS ===== */}
              {activeTab === "sections" && (
                <div className="dup-sections-editor">
                  {sections.length === 0 && (
                    <div className="empty-sections-msg">
                      <FileText size={32} />
                      <p>No sections yet. Add your first section below.</p>
                    </div>
                  )}

                  {sections.map((section, si) => (
                    <SectionEditor
                      key={section._id ?? section.sectionId ?? si}
                      section={section}
                      index={si}
                      onChange={(updated) => updateSection(si, updated)}
                      onRemove={() => removeSection(si)}
                    />
                  ))}

                  <button
                    type="button"
                    className="add-section-btn"
                    onClick={addSection}
                    title="Add a new section"
                  >
                    <Plus size={15} />
                    Add Section
                  </button>
                </div>
              )}
            </div>

            {/* ── Footer ───────────────────────────────────────────────── */}
            <div className="duplicate-popup-footer">
              <button
                className="duplicate-cancel-btn"
                onClick={onCancel}
                disabled={duplicating}
                title="Discard this copy and close"
              >
                Cancel
              </button>
              <button
                className="duplicate-confirm-btn"
                onClick={handleSubmit}
                disabled={duplicating || sections.length === 0}
                title="Save this as a new test"
              >
                <Copy size={16} />
                {duplicating ? "Duplicating…" : "Duplicate Test"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SharePopup – search + multi-select users, then invite them to a test
// ---------------------------------------------------------------------------
const SharePopup = ({ test, onCancel, onSuccess }) => {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);
  const [sharing, setSharing] = useState(false);
  const [generatedLink, setGeneratedLink] = useState(""); // INTERNAL link, keyword: INTERNAL
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_USER_URL}/user-details/getall`,
        );
        if (isMounted) setUsers(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch users:", err);
        toast.error("❌ Failed to load users");
      } finally {
        if (isMounted) setLoadingUsers(false);
      }
    };
    fetchUsers();
    return () => {
      isMounted = false;
    };
  }, []);

  const filteredUsers = users.filter((u) => {
    // Exclude currently logged-in user
    if (
      currentUser?.userDetailsId != null &&
      u.userDetailsId === currentUser.userDetailsId
    ) {
      return false;
    }

    const term = searchTerm.trim().toLowerCase();

    if (!term) return true;

    return (
      u.username?.toLowerCase().includes(term) ||
      u.email?.toLowerCase().includes(term) ||
      String(u.mobileNumber || "").includes(term)
    );
  });

  const isSelected = (id) => selectedIds.includes(id);

  const toggleUser = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  };

  const allFilteredSelected =
    filteredUsers.length > 0 &&
    filteredUsers.every((u) => selectedIds.includes(u.userDetailsId));

  const toggleSelectAllFiltered = () => {
    if (allFilteredSelected) {
      const filteredIds = filteredUsers.map((u) => u.userDetailsId);
      setSelectedIds((prev) => prev.filter((id) => !filteredIds.includes(id)));
    } else {
      const filteredIds = filteredUsers.map((u) => u.userDetailsId);
      setSelectedIds((prev) => Array.from(new Set([...prev, ...filteredIds])));
    }
  };

  const handleShare = async () => {
    if (selectedIds.length === 0) {
      toast.error("Select at least one user to share with");
      return;
    }
    setSharing(true);
    try {
      // 4th Endpoint – Invite user(s) to the test. This is the "internal"
      // sharing flow: the recipients are already registered users, so no
      // OTP step is needed — they log in and start the test directly.
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_API_USER_URL}/test/invite`,
        {
          testId: test.testId,
          userDetailsIds: selectedIds,
        },
      );
      toast.success(
        `✅ Test shared with ${selectedIds.length} user${selectedIds.length > 1 ? "s" : ""}!`,
      );
      setGeneratedLink(res.data?.data?.commonPublicLink || "");
    } catch (err) {
      console.error("Failed to share test:", err);
      toast.error("❌ Failed to share test");
    } finally {
      setSharing(false);
    }
  };

  const handleCopyGeneratedLink = async () => {
    const ok = await copyToClipboard(generatedLink);
    if (ok) {
      setLinkCopied(true);
      toast.success("🔗 Link copied to clipboard");
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      toast.error("❌ Couldn't copy link");
    }
  };

  return (
    <div className="duplicate-popup-overlay" onClick={onCancel}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div
        className="duplicate-popup duplicate-popup--medium share-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="duplicate-popup-header">
          <div className="duplicate-popup-title">
            <Share2 size={20} />
            <h2>Share "{test?.title}"</h2>
          </div>
          <button
            className="duplicate-popup-close"
            onClick={onCancel}
            title="Close without sharing"
          >
            <X size={20} />
          </button>
        </div>

        <div className="duplicate-popup-body">
          <div className="form-group">
            <label>
              <Search
                size={13}
                style={{ marginRight: 4, verticalAlign: "middle" }}
              />
              Search Users
            </label>
            <div style={{ position: "relative" }}>
              <Search
                size={15}
                style={{
                  position: "absolute",
                  left: 10,
                  top: "50%",
                  transform: "translateY(-50%)",
                  opacity: 0.5,
                }}
              />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by name, email or mobile number"
                style={{ paddingLeft: 32, width: "100%" }}
                autoFocus
              />
            </div>
          </div>

          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              margin: "4px 0 8px",
            }}
          >
            <span style={{ fontSize: 13, opacity: 0.75 }}>
              {selectedIds.length} selected
              {searchTerm ? ` · ${filteredUsers.length} match` : ""}
            </span>
            {filteredUsers.length > 0 && (
              <button
                type="button"
                className="add-section-btn"
                style={{ padding: "4px 10px", fontSize: 12 }}
                onClick={toggleSelectAllFiltered}
                title={
                  allFilteredSelected
                    ? "Unselect all visible users"
                    : "Select all visible users"
                }
              >
                <Check size={13} />
                {allFilteredSelected ? "Unselect all" : "Select all"}
              </button>
            )}
          </div>

          <div
            className="toggles-card"
            style={{
              maxHeight: 320,
              overflowY: "auto",
              padding: 0,
            }}
          >
            {loadingUsers ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: 16,
                  opacity: 0.7,
                }}
              >
                <Loader2
                  size={16}
                  className="spin"
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Loading users…
              </div>
            ) : filteredUsers.length === 0 ? (
              <div style={{ padding: 16, opacity: 0.7, fontSize: 13 }}>
                No users found{searchTerm ? ` for "${searchTerm}"` : ""}.
              </div>
            ) : (
              filteredUsers.map((u) => (
                <label
                  key={u.userDetailsId}
                  className="toggle-row"
                  style={{ cursor: "pointer" }}
                >
                  <span
                    className="toggle-row-label"
                    style={{ display: "flex", flexDirection: "column" }}
                  >
                    <span style={{ fontWeight: 500 }}>
                      {u.username || "Unnamed User"}
                    </span>
                    <span style={{ fontSize: 12, opacity: 0.65 }}>
                      {u.email}
                      {u.mobileNumber ? ` · ${u.mobileNumber}` : ""}
                    </span>
                  </span>
                  <input
                    type="checkbox"
                    checked={isSelected(u.userDetailsId)}
                    onChange={() => toggleUser(u.userDetailsId)}
                    style={{ width: 18, height: 18, cursor: "pointer" }}
                  />
                </label>
              ))
            )}
          </div>

          {generatedLink && (
            <div className="form-group" style={{ marginTop: 12 }}>
              <label>
                <LinkIcon
                  size={13}
                  style={{ marginRight: 4, verticalAlign: "middle" }}
                />
                Internal Test Link
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  readOnly
                  value={generatedLink}
                  style={{ flex: 1 }}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className="add-section-btn"
                  onClick={handleCopyGeneratedLink}
                  style={{ whiteSpace: "nowrap" }}
                  title="Copy the internal test link to clipboard"
                >
                  {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                  {linkCopied ? "Copied" : "Copy"}
                </button>
              </div>
              <p style={{ fontSize: 12, opacity: 0.65, marginTop: 6 }}>
                Invited users log in and start the test directly — no OTP
                required for internal links.
              </p>
            </div>
          )}
        </div>

        <div className="duplicate-popup-footer">
          {generatedLink ? (
            <button
              className="duplicate-confirm-btn"
              onClick={onSuccess}
              title="Close this dialog"
            >
              <Check size={16} />
              Done
            </button>
          ) : (
            <>
              <button
                className="duplicate-cancel-btn"
                onClick={onCancel}
                disabled={sharing}
                title="Close without sharing"
              >
                Cancel
              </button>
              <button
                className="duplicate-confirm-btn"
                onClick={handleShare}
                disabled={sharing || selectedIds.length === 0}
                title="Invite the selected users to this test"
              >
                <Share2 size={16} />
                {sharing
                  ? "Sharing…"
                  : `Share${selectedIds.length ? ` (${selectedIds.length})` : ""}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// PrivateLinkPopup – generate a one-off PRIVATE test link for a single email
// (3rd Endpoint: POST /test/private/link). The recipient logs in, an OTP is
// sent automatically (1st Endpoint) and must be verified (2nd Endpoint)
// before they can start the test.
// ---------------------------------------------------------------------------
const PrivateLinkPopup = ({ test, onCancel, onSuccess }) => {
  const [email, setEmail] = useState("");
  const [generating, setGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState("");
  const [linkCopied, setLinkCopied] = useState(false);
  const [linkLogs, setLinkLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(true);
  const [logsError, setLogsError] = useState("");
  const [copiedLogId, setCopiedLogId] = useState(null);

  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

  const fetchLinkLogs = async () => {
    if (!test?.testId) return;

    setLogsLoading(true);
    setLogsError("");
    try {
      const response = await axiosInstance.get(
        `${process.env.REACT_APP_API_USER_URL}/test/private/link-logs`,
        { params: { testId: test.testId } },
      );
      setLinkLogs(Array.isArray(response.data?.data) ? response.data.data : []);
    } catch (err) {
      console.error("Failed to load private link logs:", err);
      setLogsError("Couldn't load previously shared recipients.");
    } finally {
      setLogsLoading(false);
    }
  };

  useEffect(() => {
    fetchLinkLogs();
    // The popup is remounted for each selected test, so this intentionally
    // loads the current test's recipient history when the dialog opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [test?.testId]);

  const handleGenerate = async () => {
    if (!email.trim() || !isValidEmail(email.trim())) {
      toast.error("Enter a valid email address");
      return;
    }
    setGenerating(true);
    try {
      const res = await axiosInstance.post(
        `${process.env.REACT_APP_API_USER_URL}/test/private/link`,
        { testId: test.testId, email: email.trim() },
      );
      setGeneratedLink(res.data?.data?.url || "");
      await fetchLinkLogs();
      toast.success("✅ Private test link generated");
    } catch (err) {
      console.error("Failed to generate private link:", err);
      toast.error("❌ Failed to generate private link");
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = async () => {
    const ok = await copyToClipboard(generatedLink);
    if (ok) {
      setLinkCopied(true);
      toast.success("🔗 Link copied to clipboard");
      setTimeout(() => setLinkCopied(false), 2000);
    } else {
      toast.error("❌ Couldn't copy link");
    }
  };

  // Copy button for a row in "Previously Shared Employees" — link-logs now
  // returns a `url` per entry, so each past recipient's link can be
  // re-copied without generating a new one.
  const handleCopyLogUrl = async (log) => {
    if (!log.url) {
      toast.error("No link available to copy for this recipient");
      return;
    }
    const ok = await copyToClipboard(log.url);
    if (ok) {
      setCopiedLogId(log.linkLogId);
      toast.success("🔗 Link copied to clipboard");
      setTimeout(
        () => setCopiedLogId((prev) => (prev === log.linkLogId ? null : prev)),
        2000,
      );
    } else {
      toast.error("❌ Couldn't copy link");
    }
  };

  return (
    <div className="duplicate-popup-overlay" onClick={onCancel}>
      <div
        className="duplicate-popup duplicate-popup--medium share-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="duplicate-popup-header">
          <div className="duplicate-popup-title">
            <Mail size={20} />
            <h2>Generate Private Link for "{test?.title}"</h2>
          </div>
          <button
            className="duplicate-popup-close"
            onClick={onCancel}
            title="Close without generating a link"
          >
            <X size={20} />
          </button>
        </div>

        <div className="duplicate-popup-body">
          <div className="form-group">
            <label>
              <Mail
                size={13}
                style={{ marginRight: 4, verticalAlign: "middle" }}
              />
              Recipient Email
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="name@example.com"
              disabled={!!generatedLink}
              autoFocus
            />
            <p style={{ fontSize: 12, opacity: 0.65, marginTop: 6 }}>
              The recipient will log in, receive an OTP by email, and must
              verify it before starting the test.
            </p>
          </div>

          {generatedLink && (
            <div className="form-group">
              <label>
                <LinkIcon
                  size={13}
                  style={{ marginRight: 4, verticalAlign: "middle" }}
                />
                Private Test Link
              </label>
              <div style={{ display: "flex", gap: 8 }}>
                <input
                  type="text"
                  readOnly
                  value={generatedLink}
                  style={{ flex: 1 }}
                  onFocus={(e) => e.target.select()}
                />
                <button
                  type="button"
                  className="add-section-btn"
                  onClick={handleCopy}
                  style={{ whiteSpace: "nowrap" }}
                  title="Copy the private test link to clipboard"
                >
                  {linkCopied ? <Check size={14} /> : <Copy size={14} />}
                  {linkCopied ? "Copied" : "Copy"}
                </button>
              </div>
            </div>
          )}

          <section className="private-link-history" aria-live="polite">
            <div className="private-link-history__heading">
              <div>
                <h3>Previously Shared Employees</h3>
                <p>People who already received a private link for this test.</p>
              </div>
              <span className="private-link-history__count">
                {linkLogs.length}
              </span>
            </div>

            {logsLoading ? (
              <p className="private-link-history__status">
                Loading recipients…
              </p>
            ) : logsError ? (
              <p className="private-link-history__status private-link-history__status--error">
                {logsError}
              </p>
            ) : linkLogs.length === 0 ? (
              <p className="private-link-history__status">
                No private links have been shared yet.
              </p>
            ) : (
              <ul className="private-link-history__list">
                {linkLogs.map((log) => (
                  <li
                    key={log.linkLogId}
                    className="private-link-history__item"
                  >
                    <Mail size={15} aria-hidden="true" />
                    <span className="private-link-history__identity">
                      <span className="private-link-history__email">
                        {log.email || "No email recorded"}
                      </span>
                      {log.phoneNumber && (
                        <span className="private-link-history__phone">
                          {log.phoneNumber}
                        </span>
                      )}
                    </span>
                    {log.url && (
                      <button
                        type="button"
                        className="private-link-history__copy-btn"
                        onClick={() => handleCopyLogUrl(log)}
                        title="Copy this recipient's test link"
                      >
                        {copiedLogId === log.linkLogId ? (
                          <Check size={13} />
                        ) : (
                          <Copy size={13} />
                        )}
                        {copiedLogId === log.linkLogId ? "Copied" : "Copy"}
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>
        </div>

        <div className="duplicate-popup-footer">
          {generatedLink ? (
            <button
              className="duplicate-confirm-btn"
              onClick={onSuccess}
              title="Close this dialog"
            >
              <Check size={16} />
              Done
            </button>
          ) : (
            <>
              <button
                className="duplicate-cancel-btn"
                onClick={onCancel}
                disabled={generating}
                title="Close without generating a link"
              >
                Cancel
              </button>
              <button
                className="duplicate-confirm-btn"
                onClick={handleGenerate}
                disabled={generating || !email.trim()}
                title="Generate a private link and email an OTP to this address"
              >
                <Send size={16} />
                {generating ? "Generating…" : "Generate & Copy Link"}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SharedUserRow – one row of the shared-user-results list
// (GET /api/user/tests/{testId}/shared-user-results): a user and how many
// attempts they have on this test. Clicking it loads that user's attempts
// via GET /api/user/tests/shared-user-result/{testId}/{userId}.
// ---------------------------------------------------------------------------
const SharedUserRow = ({ user, isActive, isLoading, onViewAttempts }) => (
  <button
    type="button"
    className={`shared-user-row ${isActive ? "shared-user-row--active" : ""}`}
    onClick={() => onViewAttempts(user)}
    title={`View ${user.userName || "this user"}'s attempts`}
  >
    <div className="shared-user-row__identity">
      <span className="shared-user-row__name">
        {user.userName || "Unnamed User"}
      </span>
      <span className="shared-user-row__email">{user.email || "—"}</span>
    </div>
    <div className="shared-user-row__meta">
      <span className="shared-user-row__attempts" title="Total attempts">
        {user.totalAttempts ?? 0}{" "}
        {Number(user.totalAttempts) === 1 ? "attempt" : "attempts"}
      </span>
      {isLoading ? (
        <Loader2 size={14} style={{ animation: "spin 1s linear infinite" }} />
      ) : (
        <ChevronRight size={14} className="shared-user-row__chevron" />
      )}
    </div>
  </button>
);

// ---------------------------------------------------------------------------
// AttemptRow – one attempt returned by
// GET /api/user/tests/shared-user-result/{testId}/{userId}. Summarises the
// attempt and opens the full TestResults view for it.
// ---------------------------------------------------------------------------
const AttemptRow = ({ attempt, index, onViewDetails }) => {
  const sectionCount = (attempt.sections || []).length;
  return (
    <div className="attempt-row">
      <div className="attempt-row__info">
        <span className="attempt-row__title">Attempt {index + 1}</span>
        <span className="attempt-row__sub">
          {attempt.interpretation?.title ||
            attempt.recommendation?.title ||
            `${sectionCount} section${sectionCount === 1 ? "" : "s"}`}
        </span>
      </div>
      <div className="attempt-row__meta">
        <span title="Total score" className="attempt-row__score">
          <Award size={12} /> {attempt.totalScore ?? "—"}
        </span>
        <button
          type="button"
          className="attempt-row__view-btn"
          onClick={() => onViewDetails(attempt)}
          title="View full result"
        >
          <BarChart3 size={13} />
          View Full Result
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ViewDetailRow / ViewToggleBadge – tiny read-only display primitives used
// by ViewTestPopup below
// ---------------------------------------------------------------------------
const ViewDetailRow = ({ label, value }) => (
  <div className="view-detail-row">
    <span className="view-detail-label">{label}</span>
    <span className="view-detail-value">{value ?? "—"}</span>
  </div>
);

const ViewToggleBadge = ({ label, on }) => (
  <div className={`view-toggle-badge ${on ? "view-toggle-badge--on" : ""}`}>
    {on ? <Check size={13} /> : <X size={13} />}
    <span>{label}</span>
  </div>
);

// ---------------------------------------------------------------------------
// ViewTestPopup – full read-only view of a test
// (GET /api/user/tests/{testId})
// ---------------------------------------------------------------------------
const ViewTestPopup = ({ test, onCancel }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchTest = async () => {
      setLoading(true);
      setError(false);
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_USER_URL}/tests/${test.testId}`,
        );
        if (isMounted) setData(res.data?.data || null);
      } catch (err) {
        console.error("Failed to fetch test details:", err);
        if (isMounted) {
          setError(true);
          toast.error("❌ Failed to load test details");
        }
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    fetchTest();
    return () => {
      isMounted = false;
    };
  }, [test.testId]);

  const dur = data
    ? parseDurationToHM(data.testduration)
    : { hours: 0, minutes: 0 };

  return (
    <div className="duplicate-popup-overlay" onClick={onCancel}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div
        className="duplicate-popup duplicate-popup--xlarge view-test-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="duplicate-popup-header">
          <div className="duplicate-popup-title">
            <Eye size={20} />
            <h2>{data?.testTitle || test?.title || "Test Details"}</h2>
          </div>
          <button
            className="duplicate-popup-close"
            onClick={onCancel}
            title="Close"
          >
            <X size={20} />
          </button>
        </div>

        <div className="duplicate-popup-body">
          {loading ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: 16,
                opacity: 0.7,
              }}
            >
              <Loader2
                size={16}
                style={{ animation: "spin 1s linear infinite" }}
              />
              Loading test details…
            </div>
          ) : error ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 13 }}>
              Couldn't load this test's details. Please try again.
            </div>
          ) : !data ? null : (
            <div className="view-popup-columns">
              <div className="view-popup-col view-popup-col--left">
                {/* ── Overview ────────────────────────────────────────── */}
                <p className="dup-section-label">
                  <FileText
                    size={13}
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  />
                  Overview
                </p>
                <div className="view-detail-card">
                  <ViewDetailRow label="Title" value={data.testTitle} />
                  <ViewDetailRow
                    label="Description"
                    value={data.description || "—"}
                  />
                  <ViewDetailRow
                    label="Category"
                    value={data.testCategoryName}
                  />
                  <ViewDetailRow label="Test Type" value={data.testType} />
                  <ViewDetailRow
                    label="Link Expiry"
                    value={
                      data.linkExpiryDate
                        ? new Date(data.linkExpiryDate).toLocaleDateString()
                        : "Never"
                    }
                  />
                  <ViewDetailRow
                    label="Duration"
                    value={
                      dur.hours || dur.minutes
                        ? `${dur.hours}h ${dur.minutes}m`
                        : "—"
                    }
                  />
                  <ViewDetailRow label="Total Score" value={data.totalScore} />
                  <ViewDetailRow
                    label="Attempt Type"
                    value={data.testAttemptType}
                  />
                  {String(data.testAttemptType).toUpperCase() ===
                    "MULTIPLE" && (
                    <ViewDetailRow
                      label="Max Attempts"
                      value={data.maxAttempts}
                    />
                  )}
                  <ViewDetailRow
                    label="Test Used"
                    value={data.testUsed ? "Yes (has responses)" : "No"}
                  />
                  {data.commonLink && (
                    <ViewDetailRow
                      label="Public Link"
                      value={
                        <a
                          href={data.commonLink}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="view-detail-link"
                        >
                          {data.commonLink}
                        </a>
                      }
                    />
                  )}
                </div>

                {/* ── Result Visibility ───────────────────────────────── */}
                <p className="dup-section-label" style={{ marginTop: 16 }}>
                  <Eye
                    size={13}
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  />
                  Result Visibility
                </p>
                <div className="view-toggle-badge-grid">
                  <ViewToggleBadge
                    label="Show results to user"
                    on={!!data.showResultsToUser}
                  />
                  <ViewToggleBadge label="Show score" on={!!data.showScore} />
                  <ViewToggleBadge
                    label="Show recommendation"
                    on={!!data.showRecommendation}
                  />
                  <ViewToggleBadge
                    label="Show interpretation"
                    on={!!data.showInterpretation}
                  />
                  <ViewToggleBadge
                    label="Show section recommendation"
                    on={!!data.showSectionRecommendation}
                  />
                  <ViewToggleBadge
                    label="Show section interpretation"
                    on={!!data.showSectionInterpretation}
                  />
                </div>

                {/* ── Overall Interpretations ─────────────────────────── */}
                {(data.interpretations || []).length > 0 && (
                  <>
                    <p className="dup-section-label" style={{ marginTop: 16 }}>
                      Overall Interpretations
                    </p>
                    <ul className="view-options-list">
                      {data.interpretations.map((it, ii) => (
                        <li key={it.interpretationId ?? ii}>
                          <strong>{it.title}</strong> ({it.minScore}–
                          {it.maxScore}): {it.description}
                        </li>
                      ))}
                    </ul>
                  </>
                )}

                {/* ── Overall Recommendations ─────────────────────────── */}
                {(data.recommendations || []).length > 0 && (
                  <>
                    <p className="dup-section-label" style={{ marginTop: 16 }}>
                      Overall Recommendations
                    </p>
                    <ul className="view-options-list">
                      {data.recommendations.map((r, ri) => (
                        <li key={r.recommendationId ?? ri}>
                          <strong>{r.title}</strong> ({r.minScore}–{r.maxScore}
                          ): {r.recommendationText}
                        </li>
                      ))}
                    </ul>
                  </>
                )}
              </div>

              <div className="view-popup-col view-popup-col--right">
                {/* ── Sections & Questions ────────────────────────────── */}
                <p className="dup-section-label">
                  <FileText
                    size={13}
                    style={{ marginRight: 4, verticalAlign: "middle" }}
                  />
                  Sections &amp; Questions ({data.sections?.length || 0})
                </p>
                {(data.sections || []).length === 0 && (
                  <p className="no-questions-msg">No sections in this test.</p>
                )}
                {(data.sections || []).map((section, si) => (
                  <div
                    key={section.sectionId ?? si}
                    className="view-section-card"
                  >
                    <div className="view-section-header">
                      <span className="se-index">§{si + 1}</span>
                      <span className="view-section-title">
                        {section.sectionTitle || `Section ${si + 1}`}
                      </span>
                      {section.randomizeQuestions && (
                        <span className="view-mini-badge">
                          <Shuffle size={11} /> Randomized
                        </span>
                      )}
                      <span className="view-mini-badge">
                        Score: {section.totalScore ?? 0}
                      </span>
                    </div>

                    {(section.questions || []).map((q, qi) => (
                      <div
                        key={q.questionId ?? qi}
                        className="view-question-row"
                      >
                        <div className="view-question-text">
                          <strong>Q{qi + 1}.</strong> {q.questionText}
                          <span className="view-mini-badge">
                            {q.answerType}
                          </span>
                        </div>
                        {(q.answerOptions || []).length > 0 && (
                          <ul className="view-options-list">
                            {q.answerOptions.map((opt, oi) => (
                              <li key={opt.answerOptionId ?? oi}>
                                {opt.answerText}
                                {opt.optionValue
                                  ? ` (${opt.optionValue})`
                                  : ""}{" "}
                                — score {opt.score ?? 0}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    ))}

                    {(
                      section.sectionInterpretations ||
                      section.sectionInterpretation ||
                      []
                    ).length > 0 && (
                      <div className="view-subblock">
                        <p className="view-subblock-title">
                          Section Interpretations
                        </p>
                        <ul className="view-options-list">
                          {(
                            section.sectionInterpretations ||
                            section.sectionInterpretation
                          ).map((it, ii) => (
                            <li key={it.sectionInterpretationId ?? ii}>
                              <strong>{it.title}</strong> ({it.minScore}–
                              {it.maxScore}): {it.description}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}

                    {(section.sectionRecommendations || []).length > 0 && (
                      <div className="view-subblock">
                        <p className="view-subblock-title">
                          Section Recommendations
                        </p>
                        <ul className="view-options-list">
                          {section.sectionRecommendations.map((r, ri) => (
                            <li key={r.sectionRecommendationId ?? ri}>
                              <strong>{r.title}</strong> ({r.minScore}–
                              {r.maxScore}): {r.recommendationText}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="duplicate-popup-footer">
          <button
            className="duplicate-confirm-btn"
            onClick={onCancel}
            title="Close"
          >
            <Check size={16} />
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// ResultsPopup – shared-user-results for a test
// (GET /api/user/tests/{testId}/shared-user-results)
//
// Three-level drill-down:
//   1. List of users who've shared/taken this test, each with a totalAttempts
//      count.
//   2. Clicking a user fetches every attempt they've made
//      (GET /api/user/tests/shared-user-result/{testId}/{userId}) and lists
//      them.
//   3. Clicking an attempt opens the full, real results page (the same
//      <TestResults /> component the end-user sees) as a full-screen
//      overlay, driven directly off that attempt object — its shape
//      (interpretation/recommendation objects, section list with
//      totalSectionScore, etc.) is exactly what TestResults + extractResultData
//      already expect.
// ---------------------------------------------------------------------------
const ResultsPopup = ({ test, onCancel }) => {
  // Step 1: users
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [users, setUsers] = useState([]);
  const [usersError, setUsersError] = useState(false);

  // Step 2: attempts for the selected user
  const [selectedUser, setSelectedUser] = useState(null);
  const [loadingAttempts, setLoadingAttempts] = useState(false);
  const [attempts, setAttempts] = useState([]);
  const [attemptsError, setAttemptsError] = useState(false);

  // Step 3: full result detail overlay
  const [selectedAttempt, setSelectedAttempt] = useState(null);

  useEffect(() => {
    let isMounted = true;
    const fetchUsers = async () => {
      setLoadingUsers(true);
      setUsersError(false);
      try {
        const res = await axiosInstance.get(
          `${process.env.REACT_APP_API_USER_URL}/tests/${test.testId}/shared-user-results`,
        );
        if (isMounted) setUsers(res.data?.data || []);
      } catch (err) {
        console.error("Failed to fetch shared user results:", err);
        if (isMounted) {
          setUsersError(true);
          toast.error("❌ Failed to load results");
        }
      } finally {
        if (isMounted) setLoadingUsers(false);
      }
    };
    fetchUsers();
    return () => {
      isMounted = false;
    };
  }, [test.testId]);

  const handleViewAttempts = async (user) => {
    setSelectedUser(user);
    setAttempts([]);
    setAttemptsError(false);
    setLoadingAttempts(true);
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_USER_URL}/tests/shared-user-result/${test.testId}/${user.userId}`,
      );
      setAttempts(res.data?.data || []);
    } catch (err) {
      console.error("Failed to fetch user attempts:", err);
      setAttemptsError(true);
      toast.error("❌ Failed to load this user's attempts");
    } finally {
      setLoadingAttempts(false);
    }
  };

  const handleBackToUsers = () => {
    setSelectedUser(null);
    setAttempts([]);
    setAttemptsError(false);
  };

  // Full-screen result detail — takes over the whole viewport, reusing the
  // exact component end-users see for their own results.
  if (selectedAttempt) {
    return (
      <div className="result-detail-overlay">
        <button
          type="button"
          className="result-detail-overlay__close"
          onClick={() => setSelectedAttempt(null)}
          title="Back to attempts"
        >
          <X size={18} />
          Close
        </button>
        <TestResults
          results={selectedAttempt}
          hasAlreadyTaken
          onBackToDashboard={() => setSelectedAttempt(null)}
        />
      </div>
    );
  }

  return (
    <div className="duplicate-popup-overlay" onClick={onCancel}>
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      <div
        className="duplicate-popup duplicate-popup--medium share-popup"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="duplicate-popup-header">
          <div className="duplicate-popup-title">
            {selectedUser ? (
              <>
                <button
                  type="button"
                  className="results-popup-back-btn"
                  onClick={handleBackToUsers}
                  title="Back to all users"
                >
                  <ChevronRight
                    size={18}
                    style={{ transform: "rotate(180deg)" }}
                  />
                </button>
                <BarChart3 size={20} />
                <h2>
                  {selectedUser.userName || "User"}'s Attempts — "{test?.title}"
                </h2>
              </>
            ) : (
              <>
                <BarChart3 size={20} />
                <h2>Results for "{test?.title}"</h2>
              </>
            )}
          </div>
          <button
            className="duplicate-popup-close"
            onClick={onCancel}
            title="Close results"
          >
            <X size={20} />
          </button>
        </div>

        <div className="duplicate-popup-body">
          {!selectedUser ? (
            // ── Step 1: list of users who share/took this test ──
            loadingUsers ? (
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  padding: 16,
                  opacity: 0.7,
                }}
              >
                <Loader2
                  size={16}
                  className="spin"
                  style={{ animation: "spin 1s linear infinite" }}
                />
                Loading results…
              </div>
            ) : usersError ? (
              <div style={{ padding: 16, opacity: 0.7, fontSize: 13 }}>
                Couldn't load results for this test. Please try again.
              </div>
            ) : users.length === 0 ? (
              <div style={{ padding: 16, opacity: 0.7, fontSize: 13 }}>
                No one has taken this test yet.
              </div>
            ) : (
              <div className="shared-user-list">
                {users.map((u) => (
                  <SharedUserRow
                    key={u.userId}
                    user={u}
                    isActive={selectedUser?.userId === u.userId}
                    isLoading={
                      loadingAttempts && selectedUser?.userId === u.userId
                    }
                    onViewAttempts={handleViewAttempts}
                  />
                ))}
              </div>
            )
          ) : // ── Step 2: attempts for the selected user ──
          loadingAttempts ? (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: 16,
                opacity: 0.7,
              }}
            >
              <Loader2
                size={16}
                style={{ animation: "spin 1s linear infinite" }}
              />
              Loading attempts…
            </div>
          ) : attemptsError ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 13 }}>
              Couldn't load attempts for this user. Please try again.
            </div>
          ) : attempts.length === 0 ? (
            <div style={{ padding: 16, opacity: 0.7, fontSize: 13 }}>
              No attempts found for this user.
            </div>
          ) : (
            <div className="attempt-list">
              {attempts.map((attempt, i) => (
                <AttemptRow
                  key={attempt.resultId ?? i}
                  attempt={attempt}
                  index={i}
                  onViewDetails={setSelectedAttempt}
                />
              ))}
            </div>
          )}
        </div>

        <div className="duplicate-popup-footer">
          <button
            className="duplicate-confirm-btn"
            onClick={onCancel}
            title="Close results"
          >
            <Check size={16} />
            Done
          </button>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// TestManagement
// ---------------------------------------------------------------------------
// `onEditTest(testId, isTestTaken?)` — the parent should forward the
// optional second argument through to FormBuilder as its `isTestTaken`
// prop (e.g. `<FormBuilder mode="edit" testId={testId} isTestTaken={isTestTaken} />`)
// so the editor restricts adding/removing questions, sections, score
// bands, interpretations and recommendations for a test that already has
// responses.
const TestManagement = ({ onEditTest, onCreateTest }) => {
  const { isCreator } = useAuth();
  const [tests, setTests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedTests, setSelectedTests] = useState([]);
  const [sortConfig, setSortConfig] = useState({
    key: "testId",
    direction: "desc",
  });
  const [filterStatus, setFilterStatus] = useState("all");
  const [dateRange, setDateRange] = useState({ start: "", end: "" });
  const [showFilters, setShowFilters] = useState(false);

  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [testToDelete, setTestToDelete] = useState(null);

  const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);
  const [testToDuplicate, setTestToDuplicate] = useState(null);

  // Shown before opening the full editor for a test that already has
  // responses — warns that past results may be affected and that the
  // editor itself will block adding/removing questions, sections, score
  // bands, interpretations and recommendations (see FormBuilder's
  // `isTestTaken` prop / `restrictToExisting`). `pendingEditTestId` holds
  // the test the person is trying to edit while they decide.
  const [showTestTakenWarning, setShowTestTakenWarning] = useState(false);
  const [pendingEditTestId, setPendingEditTestId] = useState(null);
  const [editCheckLoading, setEditCheckLoading] = useState(false);

  const [showSharePopup, setShowSharePopup] = useState(false);
  const [testToShare, setTestToShare] = useState(null);

  const [showPrivateLinkPopup, setShowPrivateLinkPopup] = useState(false);
  const [testForPrivateLink, setTestForPrivateLink] = useState(null);

  const [showResultsPopup, setShowResultsPopup] = useState(false);
  const [testForResults, setTestForResults] = useState(null);

  const [showViewPopup, setShowViewPopup] = useState(false);
  const [testForView, setTestForView] = useState(null);

  useEffect(() => {
    fetchTests();
  }, []);

  const fetchTests = async () => {
    setLoading(true);
    try {
      const [allResponses, allTestDetails] = await Promise.all([
        axiosInstance.get(
          `${process.env.REACT_APP_API_USER_URL}/dashboard/singletestresponse`,
        ),
        axiosInstance.get(`${process.env.REACT_APP_API_USER_URL}/tests/getall`),
      ]);
      const testStats = allResponses.data?.data || [];
      const testDetails = allTestDetails.data?.data || [];
      const testDetailsMap = testDetails.reduce((acc, t) => {
        acc[t.testId] = t;
        return acc;
      }, {});
      const testsWithDetails = testStats.map((stat) => {
        const details = testDetailsMap[stat.testId];
        // `stat` (from /dashboard/singletestresponse) already carries the
        // authoritative testTitle/description/totalScore/linkExpiryDate for
        // each test. Prefer those, and only fall back to `/tests/getall`
        // (`details`) when a field isn't present on `stat` at all — don't
        // let a missing/mismatched `details` entry blank out good data.
        const linkExpiryDate =
          stat.linkExpiryDate ?? details?.linkExpiryDate ?? null;
        const expirationDate = linkExpiryDate ? new Date(linkExpiryDate) : null;
        const isExpired = expirationDate && expirationDate < new Date();
        // There's no explicit `status` field from the API — whether a test
        // can currently be taken is driven entirely by `canAttempt` (from
        // GET /tests/getall). A test only counts as "active" when it's
        // both attemptable and not past its link expiry.
        const canAttempt = details?.canAttempt ?? false;
        return {
          ...stat,
          title: stat.testTitle || details?.testTitle || "Untitled Test",
          expirationDate: linkExpiryDate,
          description: stat.description ?? details?.description ?? null,
          totalScore: stat.totalScore ?? details?.totalScore ?? 0,
          // fields not returned by singletestresponse still come from details.
          // NOTE: the API field is `commonLink` (see GET /tests/getall) —
          // it was previously read/stored as `commonPublicLink`, which
          // doesn't exist on either response, so this was always null.
          commonLink: details?.commonLink ?? stat.commonLink ?? null,
          canAttempt,
          status: isExpired ? "expired" : canAttempt ? "active" : "inactive",
          isExpired,
        };
      });
      setTests(testsWithDetails);
    } catch (error) {
      console.error("❌ Failed to fetch tests:", error);
      toast.error("❌ Failed to fetch tests");
    } finally {
      setLoading(false);
    }
  };

  const handleEditTest = async (testId) => {
    setEditCheckLoading(true);
    try {
      const res = await axiosInstance.get(
        `${process.env.REACT_APP_API_USER_URL}/tests/${testId}`,
      );
      const data = res.data?.data;
      if (data?.testUsed) {
        // Don't open the editor straight away — warn first that this test
        // already has responses, then hand off to the normal FormBuilder
        // edit flow with `isTestTaken` so it can enforce the restriction
        // (existing content stays editable; nothing new can be created).
        setPendingEditTestId(testId);
        setShowTestTakenWarning(true);
      } else {
        onEditTest && onEditTest(testId);
      }
    } catch (err) {
      console.error("Failed to check test status:", err);
      toast.error("Failed to open test for editing");
    } finally {
      setEditCheckLoading(false);
    }
  };

  const handleTestTakenWarningAccept = () => {
    setShowTestTakenWarning(false);
    onEditTest && onEditTest(pendingEditTestId, true);
    setPendingEditTestId(null);
  };
  const handleTestTakenWarningDecline = () => {
    setShowTestTakenWarning(false);
    setPendingEditTestId(null);
  };

  const filteredTests = tests.filter((test) => {
    const matchesSearch =
      test.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      test.testId.toString().includes(searchTerm);
    const matchesStatus =
      filterStatus === "all" || test.status === filterStatus;
    let matchesDateRange = true;
    if (dateRange.start && dateRange.end) {
      if (test.expirationDate) {
        const testDate = new Date(test.expirationDate);
        matchesDateRange =
          testDate >= new Date(dateRange.start) &&
          testDate <= new Date(dateRange.end);
      } else {
        matchesDateRange = false;
      }
    }
    return matchesSearch && matchesStatus && matchesDateRange;
  });

  // Extracts a comparable value for each sortable column.
  const getSortValue = (test, key) => {
    switch (key) {
      case "title":
        return (test.title || "").toLowerCase();
      case "resultCount":
        return test.resultCount ?? 0;
      case "status":
        return test.status || "";
      case "expirationDate":
        return test.expirationDate
          ? new Date(test.expirationDate).getTime()
          : 0;
      case "testId":
      default:
        return test.testId ?? 0;
    }
  };

  const toggleSort = (key) => {
    setSortConfig((prev) => {
      if (prev.key === key) {
        return { key, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  };

  const renderSortIcon = (key) => {
    if (sortConfig.key !== key) {
      return <ArrowUpDown size={12} className="sort-icon" />;
    }
    return sortConfig.direction === "asc" ? (
      <ArrowUp size={12} className="sort-icon active" />
    ) : (
      <ArrowDown size={12} className="sort-icon active" />
    );
  };

  const sortedTests = [...filteredTests].sort((a, b) => {
    const aValue = getSortValue(a, sortConfig.key);
    const bValue = getSortValue(b, sortConfig.key);
    if (aValue < bValue) return sortConfig.direction === "asc" ? -1 : 1;
    if (aValue > bValue) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  const allVisibleSelected =
    sortedTests.length > 0 &&
    sortedTests.every((t) => selectedTests.includes(t.testId));
  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedTests([]);
    } else {
      setSelectedTests(sortedTests.map((t) => t.testId));
    }
  };
  const toggleSelectTest = (testId) => {
    setSelectedTests((prev) =>
      prev.includes(testId)
        ? prev.filter((id) => id !== testId)
        : [...prev, testId],
    );
  };

  const initiateDeleteTest = (testId) => {
    setTestToDelete(testId);
    setShowDeleteConfirm(true);
  };
  const handleDeleteConfirm = async () => {
    try {
      await axiosInstance.delete(
        `${process.env.REACT_APP_API_USER_URL}/tests/${testToDelete}`,
      );
      toast.success("✅ Test deleted successfully!");
      fetchTests();
      setShowDeleteConfirm(false);
      setTestToDelete(null);
    } catch (error) {
      console.error("❌ Error deleting test:", error);
      toast.error("❌ Failed to delete test");
      setShowDeleteConfirm(false);
      setTestToDelete(null);
    }
  };
  const handleDeleteCancel = () => {
    setShowDeleteConfirm(false);
    setTestToDelete(null);
  };

  const initiateBulkDelete = () => {
    if (selectedTests.length === 0) return;
    setShowBulkDeleteConfirm(true);
  };
  const handleBulkDeleteConfirm = async () => {
    try {
      await Promise.all(
        selectedTests.map((testId) =>
          axiosInstance.delete(
            `${process.env.REACT_APP_API_USER_URL}/tests/${testId}`,
          ),
        ),
      );
      toast.success(`✅ Successfully deleted ${selectedTests.length} test(s)!`);
      setSelectedTests([]);
      fetchTests();
      setShowBulkDeleteConfirm(false);
    } catch (error) {
      console.error("❌ Error deleting tests:", error);
      toast.error("❌ Failed to delete some tests");
      setShowBulkDeleteConfirm(false);
    }
  };
  const handleBulkDeleteCancel = () => {
    setShowBulkDeleteConfirm(false);
  };

  const initiateDuplicateTest = (test) => {
    setTestToDuplicate(test);
    setShowDuplicatePopup(true);
  };
  const handleDuplicateSuccess = () => {
    setShowDuplicatePopup(false);
    setTestToDuplicate(null);
    fetchTests();
  };
  const handleDuplicateCancel = () => {
    setShowDuplicatePopup(false);
    setTestToDuplicate(null);
  };

  const initiateShareTest = (test) => {
    setTestToShare(test);
    setShowSharePopup(true);
  };
  const handleShareSuccess = () => {
    setShowSharePopup(false);
    setTestToShare(null);
  };
  const handleShareCancel = () => {
    setShowSharePopup(false);
    setTestToShare(null);
  };

  const initiatePrivateLink = (test) => {
    setTestForPrivateLink(test);
    setShowPrivateLinkPopup(true);
  };
  const handlePrivateLinkSuccess = () => {
    setShowPrivateLinkPopup(false);
    setTestForPrivateLink(null);
  };
  const handlePrivateLinkCancel = () => {
    setShowPrivateLinkPopup(false);
    setTestForPrivateLink(null);
  };

  const initiateViewResults = (test) => {
    setTestForResults(test);
    setShowResultsPopup(true);
  };
  const handleResultsCancel = () => {
    setShowResultsPopup(false);
    setTestForResults(null);
  };

  const initiateViewTest = (test) => {
    setTestForView(test);
    setShowViewPopup(true);
  };
  const handleViewCancel = () => {
    setShowViewPopup(false);
    setTestForView(null);
  };

  // 5th Endpoint's flow relies on the PUBLIC link stored on the Test itself
  // (test.commonPublicLink), not on generating a new one per click.
  const handleCopyPublicLink = async (test) => {
    let link = test.commonLink;

    if (!link) {
      toast.error("No public link is available for this test yet");
      return;
    }

    // Extract URL from markdown: [text](url)
    const match = link.match(/\((https?:\/\/[^)]+)\)/);
    if (match) {
      link = match[1];
    }

    const ok = await copyToClipboard(link);

    if (ok) toast.success("🔗 Public link copied to clipboard");
    else toast.error("❌ Couldn't copy link");
  };

  const exportTests = () => {
    const csvContent = [
      ["Test ID", "Title", "Status", "Responses", "Expiration Date"],
      ...sortedTests.map((test) => [
        test.testId,
        test.title,
        test.status,
        test.resultCount,
        test.expirationDate
          ? new Date(test.expirationDate).toLocaleDateString()
          : "No Expiration",
      ]),
    ]
      .map((row) => row.join(","))
      .join("\n");
    const blob = new Blob([csvContent], { type: "text/csv" });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "tests-export.csv";
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const clearAllFilters = () => {
    setSearchTerm("");
    setFilterStatus("all");
    setDateRange({ start: "", end: "" });
    setShowFilters(false);
  };
  const hasActiveFilters =
    searchTerm || filterStatus !== "all" || dateRange.start || dateRange.end;

  // Row action icons, shared between the table body render below.
  const renderTestActions = (test) => (
    <div className="action-buttons">
      <ActionIconButton
        variant="view"
        label="View Test"
        onClick={() => initiateViewTest(test)}
      />
      <ActionIconButton
        variant="edit"
        label="Edit Test"
        disabled={editCheckLoading}
        onClick={() => handleEditTest(test.testId)}
      />
      <ActionIconButton
        variant="results"
        label="View Results"
        onClick={() => initiateViewResults(test)}
      />
      <ActionIconButton
        variant="duplicate"
        label="Duplicate Test"
        onClick={() => initiateDuplicateTest(test)}
      />
      <ActionIconButton
        variant="share"
        label="Share Internally (copy link)"
        onClick={() => initiateShareTest(test)}
      />
      <ActionIconButton
        variant="private-link"
        label="Generate Private Link"
        onClick={() => initiatePrivateLink(test)}
      />
      <ActionIconButton
        variant="public-link"
        label="Copy Public Link"
        onClick={() => handleCopyPublicLink(test)}
      />
      <ActionIconButton
        variant="delete"
        label="Delete Test"
        onClick={() => initiateDeleteTest(test.testId)}
      />
    </div>
  );

  if (loading) return <LoadingScreen />;
  const testToDeleteData = tests.find((test) => test.testId === testToDelete);

  return (
    <div className="test-management">
      {/* Header: title, stats, search, filters and actions share one row */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1>Test Management</h1>
          <p>Manage and organize your personality tests</p>
        </div>

        <div className="header-stats">
          <div className="mini-stat mini-stat--total">
            <div className="mini-stat-icon">
              <FileText size={14} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">{tests.length}</span>
              <span className="mini-stat-label">Total Tests</span>
            </div>
          </div>

          <div className="mini-stat mini-stat--active">
            <div className="mini-stat-icon">
              <CheckCircle size={14} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">
                {tests.filter((t) => t.status === "active").length}
              </span>
              <span className="mini-stat-label">Active</span>
            </div>
          </div>

          <div className="mini-stat mini-stat--responses">
            <div className="mini-stat-icon">
              <Users size={14} />
            </div>
            <div className="mini-stat-body">
              <span className="mini-stat-value">
                {tests.reduce((total, t) => total + t.resultCount, 0)}
              </span>
              <span className="mini-stat-label">Responses</span>
            </div>
          </div>
        </div>

        <div className="header-actions">
          <ButtonWithIcon
            icon="lucide:refresh-cw"
            text="Refresh"
            onClick={fetchTests}
            className="outline"
            title="Reload the test list"
          />
          {isCreator && (
            <ButtonWithIcon
              className="submit-button"
              onClick={onCreateTest}
              text="Create Test"
              icon={"line-md:plus"}
              title="Create a new test from scratch"
            />
          )}
        </div>
      </div>

      <div className="history-controls">
        <div className="search-container">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search by title or ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {(hasActiveFilters || selectedTests.length > 0) && (
          <div className="active-filters-row">
            {hasActiveFilters && (
              <div className="active-filters">
                <span className="showing-results">
                  Showing {filteredTests.length} of {tests.length} tests
                </span>
                {searchTerm && (
                  <span className="active-filter-tag">
                    Search: "{searchTerm}"
                    <button
                      onClick={() => setSearchTerm("")}
                      title="Clear search term"
                    >
                      <X size={12} />
                    </button>
                  </span>
                )}
                {filterStatus !== "all" && (
                  <span className="active-filter-tag">
                    Status: {filterStatus}
                    <button
                      onClick={() => setFilterStatus("all")}
                      title="Clear status filter"
                    >
                      <X size={12} />
                    </button>
                  </span>
                )}
                {(dateRange.start || dateRange.end) && (
                  <span className="active-filter-tag">
                    Date Range
                    <button
                      onClick={() => setDateRange({ start: "", end: "" })}
                      title="Clear date range filter"
                    >
                      <X size={12} />
                    </button>
                  </span>
                )}
              </div>
            )}
            {selectedTests.length > 0 && (
              <div className="bulk-actions">
                <span className="selected-count">
                  {selectedTests.length} selected
                </span>
                <button
                  className="bulk-delete-btn"
                  onClick={initiateBulkDelete}
                  title={`Delete ${selectedTests.length} selected test${selectedTests.length > 1 ? "s" : ""}`}
                >
                  <Trash2 size={14} /> Delete Selected
                </button>
              </div>
            )}
          </div>
        )}

        <div className="filter-controls-group">
          <div className="filter-controls">
            <div className="filter-dropdown">
              <Filter size={14} />
              <select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                title="Filter tests by status"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <button
              className={`filter-toggle-btn ${showFilters ? "active" : ""}`}
              onClick={() => setShowFilters(!showFilters)}
              title={
                showFilters ? "Hide filter options" : "Show filter options"
              }
            >
              <Calendar size={14} /> Date
              <ChevronDown
                size={14}
                className={`chevron ${showFilters ? "rotated" : ""}`}
              />
              {(dateRange.start || dateRange.end) && (
                <span className="filter-indicator" />
              )}
            </button>

            <button
              className="export-btn"
              onClick={exportTests}
              title="Export tests to CSV"
            >
              <Download size={14} /> Export
            </button>

            {hasActiveFilters && (
              <button
                className="clear-filters-btn"
                onClick={clearAllFilters}
                title="Clear all search and filter criteria"
              >
                <RotateCcw size={12} /> Clear
              </button>
            )}
          </div>

          {showFilters && (
            <div className="expanded-filters">
              <div className="filter-group">
                <Calendar size={14} title="Expiration Date Range" />
                <div className="date-range-inputs">
                  <input
                    type="date"
                    value={dateRange.start}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, start: e.target.value })
                    }
                  />
                  <span className="date-separator">to</span>
                  <input
                    type="date"
                    value={dateRange.end}
                    onChange={(e) =>
                      setDateRange({ ...dateRange, end: e.target.value })
                    }
                  />
                </div>
              </div>
              {hasActiveFilters && (
                <button
                  className="clear-filters-btn"
                  onClick={clearAllFilters}
                  title="Clear all search and filter criteria"
                >
                  <X size={14} /> Clear All
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Table — compact row-based layout matching UserHistory */}
      <div className="test-stats-section">
        <div className="table-container">
          {sortedTests.length > 0 ? (
            <div className="test-list">
              <div className="test-row test-table-header">
                <div className="test-row__checkbox">
                  <input
                    type="checkbox"
                    checked={allVisibleSelected}
                    onChange={toggleSelectAll}
                    aria-label="Select all tests"
                  />
                </div>
                <div className="test-row__title-cell">
                  <button
                    type="button"
                    className="sort-btn"
                    onClick={() => toggleSort("title")}
                  >
                    Test Title
                    {renderSortIcon("title")}
                  </button>
                </div>
                <div className="test-row__responses">
                  <button
                    type="button"
                    className="sort-btn"
                    onClick={() => toggleSort("resultCount")}
                  >
                    Responses
                    {renderSortIcon("resultCount")}
                  </button>
                </div>
                <div className="test-row__status">
                  <button
                    type="button"
                    className="sort-btn"
                    onClick={() => toggleSort("status")}
                  >
                    Status
                    {renderSortIcon("status")}
                  </button>
                </div>
                <div className="test-row__expires">
                  <button
                    type="button"
                    className="sort-btn"
                    onClick={() => toggleSort("expirationDate")}
                  >
                    Expires
                    {renderSortIcon("expirationDate")}
                  </button>
                </div>
                <div className="test-row__action">
                  <span className="header-label">Actions</span>
                </div>
              </div>

              {sortedTests.map((test) => (
                <div key={test.testId} className="test-row">
                  <div className="test-row__checkbox">
                    <input
                      type="checkbox"
                      checked={selectedTests.includes(test.testId)}
                      onChange={() => toggleSelectTest(test.testId)}
                      aria-label={`Select ${test.title}`}
                    />
                  </div>

                  <div className="test-row__title-cell">
                    <div className="test-row__icon">
                      <FileText size={15} />
                    </div>
                    <div className="test-row__title-content">
                      <h3 className="test-title">{test.title}</h3>
                      <p className="test-description">
                        {test.description || `Test ID: ${test.testId}`}
                      </p>
                    </div>
                  </div>

                  <div className="test-row__responses">
                    <div className="response-cell">
                      <span className="response-count">{test.resultCount}</span>
                      <span className="response-label">responses</span>
                    </div>
                  </div>

                  <div className="test-row__status">
                    <span className={`status-badge ${test.status}`}>
                      {test.status.charAt(0).toUpperCase() +
                        test.status.slice(1)}
                    </span>
                  </div>

                  <div className="test-row__expires" data-label="Expires">
                    <span
                      className={`date-cell ${test.isExpired ? "expired" : ""}`}
                    >
                      {test.expirationDate
                        ? new Date(test.expirationDate).toLocaleDateString()
                        : "Never"}
                    </span>
                  </div>

                  <div className="test-row__action">
                    {renderTestActions(test)}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="empty-state">
              <FileText size={48} />
              <h3>No Tests Found</h3>
              <p>
                {searchTerm || filterStatus !== "all"
                  ? "Try adjusting your search or filters"
                  : "Create your first test to get started"}
              </p>
              {!searchTerm && filterStatus === "all" && isCreator && (
                <button
                  className="create-test-btns"
                  onClick={onCreateTest}
                  title="Create a new test from scratch"
                >
                  <FileText size={16} /> Create Test
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      <WarningPopup
        isOpen={showDeleteConfirm}
        title="Delete Test"
        message={`Are you sure you want to delete the test "${testToDeleteData?.title || "Unknown Test"}"? This action cannot be undone.`}
        acceptText="Delete"
        declineText="Cancel"
        onAccept={handleDeleteConfirm}
        onDecline={handleDeleteCancel}
        onClose={handleDeleteCancel}
      />
      <WarningPopup
        isOpen={showBulkDeleteConfirm}
        title="Delete Multiple Tests"
        message={`Are you sure you want to delete ${selectedTests.length} test(s)? This action cannot be undone.`}
        acceptText="Delete All"
        declineText="Cancel"
        onAccept={handleBulkDeleteConfirm}
        onDecline={handleBulkDeleteCancel}
        onClose={handleBulkDeleteCancel}
      />

      {showDuplicatePopup && testToDuplicate && (
        <DuplicatePopup
          test={testToDuplicate}
          onCancel={handleDuplicateCancel}
          onSuccess={handleDuplicateSuccess}
        />
      )}
      <WarningPopup
        isOpen={showTestTakenWarning}
        title="This test already has responses"
        message="One or more people have already taken this test, so editing it may affect their existing scores and results. You'll still be able to update the wording, options, scores, interpretations and recommendations that already exist — but you won't be able to add new questions, sections, score bands, interpretations or recommendations, or remove any of the existing ones."
        acceptText="Continue to edit"
        declineText="Cancel"
        onAccept={handleTestTakenWarningAccept}
        onDecline={handleTestTakenWarningDecline}
        onClose={handleTestTakenWarningDecline}
      />
      {showSharePopup && testToShare && (
        <SharePopup
          test={testToShare}
          onCancel={handleShareCancel}
          onSuccess={handleShareSuccess}
        />
      )}
      {showPrivateLinkPopup && testForPrivateLink && (
        <PrivateLinkPopup
          test={testForPrivateLink}
          onCancel={handlePrivateLinkCancel}
          onSuccess={handlePrivateLinkSuccess}
        />
      )}
      {showResultsPopup && testForResults && (
        <ResultsPopup test={testForResults} onCancel={handleResultsCancel} />
      )}
      {showViewPopup && testForView && (
        <ViewTestPopup test={testForView} onCancel={handleViewCancel} />
      )}
    </div>
  );
};

export default TestManagement;
